import { useState, useEffect, useMemo } from "react";
import { Accordion, Button, Modal } from "react-bootstrap";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import useApi from "../../../hooks/apiService";
import { useFeature } from "../../../hooks/featureContext";
import {
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
} from "@tanstack/react-table";
import Tabelas from "../../../components/table";
import DeleteConfirmationModal from "../../../components/modalDelete";
import ToggleFeatureModal from "./toggleFeatureModal";
import { InputSelect } from "@2solve/tosolve-inputs";

function FeaturesAccordion({
  features,
  onShowModal,
  onEditFeature,
  onDeleteFeature,
  deleteData,
  updateData,
  onDataEdited,
}) {
  const { t } = useTranslation();
  const { fetchData, postData } = useApi();
  const { refreshFeatures } = useFeature();
  const [allFeatureAttributes, setAllFeatureAttributes] = useState([]);
  const [showAddAttributeModal, setShowAddAttributeModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [showDeleteAttributeModal, setShowDeleteAttributeModal] =
    useState(false);
  const [attributeToDelete, setAttributeToDelete] = useState(null);
  const [attributeNameToDelete, setAttributeNameToDelete] = useState(null);
  const [showDeleteFeatureModal, setShowDeleteFeatureModal] = useState(false);
  const [featureToDelete, setFeatureToDelete] = useState(null);
  const [featureNameToDelete, setFeatureNameToDelete] = useState(null);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [featureToToggle, setFeatureToToggle] = useState(null);

  const fetchAllFeatureAttributes = async () => {
    try {
      const res = await fetchData("/feature-attributes");
      setAllFeatureAttributes(res.data || []);
    } catch (err) {
      console.error("Erro ao buscar feature-attributes", err);
    }
  };

  const fetchAvailableAttributes = async () => {
    try {
      const res = await fetchData("/attributes");
      setAvailableAttributes(res.data || []);
    } catch (err) {
      console.error("Erro ao buscar atributos disponíveis", err);
    }
  };

  useEffect(() => {
    fetchAllFeatureAttributes();
  }, []);

  const getAttributesForFeature = (featureId) => {
    return allFeatureAttributes.filter((attr) => attr.feature.id === featureId);
  };

  const handleAddAttribute = (feature) => {
    setSelectedFeature(feature);
    fetchAvailableAttributes();
    setSelectedAttributes([]);
    setShowAddAttributeModal(true);
  };

  const handleSaveAttributes = async () => {
    if (!selectedFeature || selectedAttributes.length === 0) return;

    try {
      for (const attrId of selectedAttributes) {
        await postData("/feature-attributes", {
          feature_id: selectedFeature.id,
          attribute_id: attrId,
        });
      }
      fetchAllFeatureAttributes();
      setShowAddAttributeModal(false);
    } catch (err) {
      console.error("Erro ao adicionar atributos", err);
    }
  };

  const handleRemoveAttribute = (featureId, attributeId, attributeName) => {
    setAttributeToDelete({ featureId, attributeId });
    setAttributeNameToDelete(attributeName);
    setShowDeleteAttributeModal(true);
  };

  const confirmDeleteAttribute = async () => {
    if (!attributeToDelete) return;

    try {
      const association = allFeatureAttributes.find(
        (attr) =>
          attr.feature.id === attributeToDelete.featureId &&
          attr.attribute.id === attributeToDelete.attributeId,
      );
      if (association) {
        await deleteData(`/feature-attributes/${association.id}`);
        fetchAllFeatureAttributes();
      }
    } catch (err) {
      console.error("Erro ao remover atributo", err);
    }

    setShowDeleteAttributeModal(false);
    setAttributeToDelete(null);
    setAttributeNameToDelete(null);
  };

  const handleDeleteFeature = (feature) => {
    setFeatureToDelete(feature.id);
    setFeatureNameToDelete(feature.name);
    setShowDeleteFeatureModal(true);
  };

  const confirmDeleteFeature = async () => {
    if (!featureToDelete) return;

    try {
      await onDeleteFeature(featureToDelete);
    } catch (err) {
      console.error("Erro ao deletar feature", err);
    }

    setShowDeleteFeatureModal(false);
    setFeatureToDelete(null);
    setFeatureNameToDelete(null);
  };

  const handleToggleFeature = (feature) => {
    setFeatureToToggle(feature);
    setShowToggleModal(true);
  };

  const confirmToggleFeature = async () => {
    if (!featureToToggle) return;

    try {
      await updateData(`/features/${featureToToggle.id}`, {
        is_enabled: !featureToToggle.is_enabled,
      });
      onDataEdited();
      refreshFeatures();
    } catch (err) {
      console.error("Erro ao alterar status da feature", err);
    }

    setShowToggleModal(false);
    setFeatureToToggle(null);
  };

  const DATA_TYPE_LABEL = {
    S: t("Texto"),
    N: t("Numérico"),
    B: t("Booleano"),
  };

  const AttributesTable = ({ attributes, featureId }) => {
    const [sorting, setSorting] = useState([]);

    const columns = [
      {
        header: t("Nome"),
        accessorKey: "attribute.name",
        cell: (info) => info.getValue(),
      },
      {
        header: t("Slug"),
        accessorKey: "attribute.slug",
        cell: (info) => info.getValue(),
      },
      {
        header: t("Tipo"),
        accessorKey: "attribute.data_type",
        cell: (info) => DATA_TYPE_LABEL[info.getValue()] || info.getValue(),
      },
      {
        header: t("Obrigatório"),
        accessorKey: "attribute.is_required",
        cell: (info) => (info.getValue() ? t("Sim") : t("Não")),
      },
      {
        header: t("Recurso"),
        accessorKey: "attribute.resource.name",
        cell: (info) => info.getValue(),
      },
      {
        id: "acoes",
        header: t("Ações"),
        cell: (info) => (
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() =>
              handleRemoveAttribute(
                featureId,
                info.row.original.attribute.id,
                info.row.original.attribute.name,
              )
            }
          >
            <i className="bi bi-trash3"></i>
          </Button>
        ),
      },
    ];

    const table = useReactTable({
      data: attributes,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      state: { sorting },
      onSortingChange: setSorting,
      defaultColumn: {
        sortDescFirst: false,
      },
    });

    return <Tabelas fixLastColumn={true} table={table} />;
  };

  const filteredAttributeOptions = useMemo(() => {
    if (!selectedFeature) return [];

    // atributos já ligados à feature
    const usedAttributeIds = allFeatureAttributes
      .filter((fa) => fa.feature.id === selectedFeature.id)
      .map((fa) => fa.attribute.id);

    return availableAttributes
      .filter((attr) => !usedAttributeIds.includes(attr.id))
      .map((attr) => ({
        value: attr.id,
        label: `${attr.name} (${DATA_TYPE_LABEL[attr.data_type] || attr.data_type})`,
      }));
  }, [availableAttributes, allFeatureAttributes, selectedFeature]);

  return (
    <>
      <div className="mb-3">
        <Button onClick={onShowModal} className="btn-primary">
          <i className="bi bi-plus-circle"></i> {t("add_feature")}
        </Button>
      </div>
      <Accordion>
        {features.map((feature) => (
          <Accordion.Item key={feature.id} eventKey={feature.id.toString()}>
            <div className="accordion-cabecalho">
              <Accordion.Header>
                <div className="pe-3 d-flex justify-content-between align-items-center w-100">
                  <span>{feature.name} </span>
                  <div className="gap-2 d-flex">
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleFeature(feature);
                      }}
                      className="me-2"
                      title={
                        feature.is_enabled ? t("Desabilitar") : t("Habilitar")
                      }
                    >
                      <i
                        className={`bi ${feature.is_enabled ? "bi-check-square" : "bi-dash-square"}`}
                      ></i>
                    </span>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditFeature(feature);
                      }}
                      className="me-2"
                    >
                      <i className="bi bi-pencil-square"></i>
                    </span>
                    <span
                      className="me-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddAttribute(feature);
                      }}
                    >
                      <i className="bi bi-plus-circle"></i>{" "}
                    </span>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFeature(feature);
                      }}
                      className="me-2"
                    >
                      <i className="bi bi-trash3"></i>
                    </span>
                  </div>
                </div>
              </Accordion.Header>
            </div>
            <Accordion.Body className="w-100 accordion-content">
              {feature.description && (
                <>
                  Descrição:{" "}
                  <span className="text-body-secondary text-break">
                    {feature.description}
                  </span>
                </>
              )}
              <AttributesTable
                attributes={getAttributesForFeature(feature.id)}
                featureId={feature.id}
              />
              {getAttributesForFeature(feature.id).length === 0 && (
                <div className="text-center p-3">
                  {t("no_associated_attributes")}
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>

      {/* Modal para adicionar atributos */}
      <Modal
        show={showAddAttributeModal}
        onHide={() => setShowAddAttributeModal(false)}
        centered
        backdrop
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>{t("add_attributes_to_feature")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InputSelect
            id={"attribute"}
            value={selectedAttributes}
            options={filteredAttributeOptions}
            isMultiple={true}
            selectAll={false}
            closeOnSelect={false}
            IsClearable={false}
            required={true}
            placeholder={t("Selecione os atributos")}
            onChange={(_, value) => {
              setSelectedAttributes(value);
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAddAttributeModal(false)}
          >
            {t("button_cancel")}
          </Button>
          <Button variant="primary" onClick={handleSaveAttributes}>
            {t("button_save")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de confirmação para deletar atributo */}
      <Modal
        show={showDeleteAttributeModal}
        onHide={() => setShowDeleteAttributeModal(false)}
        centered
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>{t("Confirmar exclusão")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("Deseja remover a ligação com o atributo")}{" "}
          <strong>{attributeNameToDelete}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteAttributeModal(false)}
          >
            {t("button_cancel")}
          </Button>
          <Button variant="danger" onClick={confirmDeleteAttribute}>
            {t("Remover")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de confirmação para deletar feature */}
      <DeleteConfirmationModal
        show={showDeleteFeatureModal}
        onClose={() => setShowDeleteFeatureModal(false)}
        onConfirm={confirmDeleteFeature}
        pagina={t("feature")}
        name={featureNameToDelete}
      />

      {/* Modal para habilitar/desabilitar feature */}
      <ToggleFeatureModal
        show={showToggleModal}
        onClose={() => setShowToggleModal(false)}
        onConfirm={confirmToggleFeature}
        featureName={featureToToggle?.name}
        currentStatus={featureToToggle?.is_enabled}
      />
    </>
  );
}

FeaturesAccordion.propTypes = {
  features: PropTypes.array.isRequired,
  onShowModal: PropTypes.func.isRequired,
  onEditFeature: PropTypes.func.isRequired,
  onDeleteFeature: PropTypes.func.isRequired,
  deleteData: PropTypes.func,
  updateData: PropTypes.func,
  onDataEdited: PropTypes.func,
};

export default FeaturesAccordion;
